import { useRouter } from 'next/router';

import { Box, FormField, FormHelp, Label, Select } from '@zillow/constellation-10';

import { useScreenSize } from '../../../hooks';
import { updateRoomForRentGroup } from '../../../hooks/queries/listingDetailsCache';
import { useGetListingDetails } from '../../../hooks/queries/properties';
import { useFieldSave } from '../../../hooks/useFieldSave';
import { useMultiEditMutation } from '../../../hooks/useMultiEditMutation';
import { getRoomForRentFlowObj } from '../../../utils/roomForRentUtils';

const ROOMMATE_COUNT_OPTIONS = [
  { label: 'Select option', value: '' },
  { label: '0', value: 0 },
  { label: '1', value: 1 },
  { label: '2', value: 2 },
  { label: '3', value: 3 },
  { label: '4', value: 4 },
  { label: '5+', value: '5+' },
];

const RoommateCountFormField = () => {
  const router = useRouter();
  const { isScreenDesktop } = useScreenSize();

  const listingAlias = router.query.listingAlias as string;
  const { data: listingDetails } = useGetListingDetails(listingAlias);

  const { isRyanFlow } = getRoomForRentFlowObj(listingDetails?.forRentByType);
  const mode = isRyanFlow ? 'detailed' : 'total';

  const roommateRequirements = listingDetails?.roomForRent?.currentRoommatesDetails;

  // For total mode
  const initialTotalCount = roommateRequirements?.roommateTotalCount ?? null;

  // For detailed mode
  const initialMaleCount = roommateRequirements?.roommateMaleCount ?? null;
  const initialFemaleCount = roommateRequirements?.roommateFemaleCount ?? null;
  const initialNonBinaryCount = roommateRequirements?.roommateNonBinaryCount ?? null;
  const initialPreferNotToIdentifyCount = roommateRequirements?.roommatePreferNotToIdentifyCount ?? null;

  const updateMutation = useMultiEditMutation({
    applyToCache: ({ postData }) => updateRoomForRentGroup('currentRoommatesDetails', postData.property.roomForRent),
  });

  const totalCountField = useFieldSave({
    fieldName: 'roommateTotalCount',
    initialValue: initialTotalCount,
    mutation: updateMutation,
    buildMutationVariables: (value) => ({
      postData: { property: { listingAlias, roomForRent: { roommateTotalCount: String(value) } } },
    }),
  });

  const maleCountField = useFieldSave({
    fieldName: 'roommateMaleCount',
    initialValue: initialMaleCount,
    mutation: updateMutation,
    buildMutationVariables: (value) => ({
      postData: { property: { listingAlias, roomForRent: { roommateMaleCount: String(value) } } },
    }),
    autoSaveOnChange: true,
  });

  const femaleCountField = useFieldSave({
    fieldName: 'roommateFemaleCount',
    initialValue: initialFemaleCount,
    mutation: updateMutation,
    buildMutationVariables: (value) => ({
      postData: { property: { listingAlias, roomForRent: { roommateFemaleCount: String(value) } } },
    }),
    autoSaveOnChange: true,
  });

  const nonBinaryCountField = useFieldSave({
    fieldName: 'roommateNonBinaryCount',
    initialValue: initialNonBinaryCount,
    mutation: updateMutation,
    buildMutationVariables: (value) => ({
      postData: {
        property: { listingAlias, roomForRent: { roommateNonBinaryCount: String(value) } },
      },
    }),
    autoSaveOnChange: true,
  });

  const preferNotToIdentifyCountField = useFieldSave({
    fieldName: 'roommatePreferNotToIdentifyCount',
    initialValue: initialPreferNotToIdentifyCount,
    mutation: updateMutation,
    buildMutationVariables: (value) => ({
      postData: {
        property: {
          listingAlias,
          roomForRent: { roommatePreferNotToIdentifyCount: String(value) },
        },
      },
    }),
    autoSaveOnChange: true,
  });

  if (mode === 'total') {
    return (
      <FormField
        label={<Label>Total number of roommates</Label>}
        error={!!totalCountField.error}
        description={<FormHelp>{totalCountField.error}</FormHelp>}
        control={
          <Select
            size={isScreenDesktop ? 'lg' : 'md'}
            name="roommateTotalCount"
            value={String(totalCountField.value)}
            onChange={(e) => totalCountField.onChange(String(e.target.value))}
            onBlur={totalCountField.onBlur}
          >
            {ROOMMATE_COUNT_OPTIONS.map(({ label, value }) => (
              <option key={label} value={value}>
                {label}
              </option>
            ))}
          </Select>
        }
      />
    );
  }

  // Detailed mode - 4 dropdowns
  return (
    <Box data-testid="RoommateCountFormField-Detailed">
      <FormField
        label={<Label>Male</Label>}
        error={!!maleCountField.error}
        description={<FormHelp>{maleCountField.error}</FormHelp>}
        control={
          <Select
            size={isScreenDesktop ? 'lg' : 'md'}
            name="roommateMaleCount"
            value={String(maleCountField.value)}
            onChange={(e) => maleCountField.onChange(String(e.target.value))}
          >
            {ROOMMATE_COUNT_OPTIONS.map(({ label, value }) => (
              <option key={label} value={value}>
                {label}
              </option>
            ))}
          </Select>
        }
      />

      <Box css={{ marginTop: '400' }}>
        <FormField
          label={<Label>Female</Label>}
          error={!!femaleCountField.error}
          description={<FormHelp>{femaleCountField.error}</FormHelp>}
          control={
            <Select
              size={isScreenDesktop ? 'lg' : 'md'}
              name="roommateFemaleCount"
              value={String(femaleCountField.value)}
              onChange={(e) => femaleCountField.onChange(String(e.target.value))}
            >
              {ROOMMATE_COUNT_OPTIONS.map(({ label, value }) => (
                <option key={label} value={value}>
                  {label}
                </option>
              ))}
            </Select>
          }
        />
      </Box>

      <Box css={{ marginTop: '400' }}>
        <FormField
          label={<Label>Non-binary</Label>}
          error={!!nonBinaryCountField.error}
          description={<FormHelp>{nonBinaryCountField.error}</FormHelp>}
          control={
            <Select
              size={isScreenDesktop ? 'lg' : 'md'}
              name="roommateNonBinaryCount"
              value={String(nonBinaryCountField.value)}
              onChange={(e) => nonBinaryCountField.onChange(String(e.target.value))}
            >
              {ROOMMATE_COUNT_OPTIONS.map(({ label, value }) => (
                <option key={label} value={value}>
                  {label}
                </option>
              ))}
            </Select>
          }
        />
      </Box>

      <Box css={{ marginTop: '400' }}>
        <FormField
          label={<Label>Prefer not to identify</Label>}
          error={!!preferNotToIdentifyCountField.error}
          description={<FormHelp>{preferNotToIdentifyCountField.error}</FormHelp>}
          control={
            <Select
              size={isScreenDesktop ? 'lg' : 'md'}
              name="roommatePreferNotToIdentifyCount"
              value={String(preferNotToIdentifyCountField.value)}
              onChange={(e) => preferNotToIdentifyCountField.onChange(String(e.target.value))}
            >
              {ROOMMATE_COUNT_OPTIONS.map(({ label, value }) => (
                <option key={label} value={value}>
                  {label}
                </option>
              ))}
            </Select>
          }
        />
      </Box>
    </Box>
  );
};

export default RoommateCountFormField;
